import React from "react";
import {
	Box,
	Typography,
	Avatar,
	Tabs,
	Tab,
  
	Paper,
	IconButton,
	TextField,
	Button,
} from "@mui/material";
import SendIcon from '@mui/icons-material/ArrowForwardIos';
import AttachFileIcon from '@mui/icons-material/AttachFile';

const conversations = [
	{
		id: 1,
		title: "Appointment reminder",
		subtitle: "NeighborCare Health",
		preview: "Hello Ms. Jane Cooper\nI'd like to discuss your recent test results and follow up on the medication adjustments...",
		time: "Jul 23, 9:30 AM",
	},
	{
		id: 2,
		title: "Appointment follow-up question",
		subtitle: "Dr. Williams: I'd like to discuss your...",
		preview: "Good morning Dr. Williams, Thank you for the update...",
		time: "Jul 23, 10:45 AM",
	},
];

const MessageBubble: React.FC<{
	text: string;
	time?: string;
	from?: "me" | "them";
	avatar?: string;
}> = ({ text, time, from = "them", avatar }) => {
	const isMe = from === "me";
	return (
		<Box display="flex" gap={2} alignItems="flex-start" sx={{ mb: 2 }}>
			{!isMe && (
				<Avatar src={avatar} sx={{ width: 36, height: 36 }} />
			)}
			<Box sx={{ flex: 1 }}>
				<Paper
					elevation={0}
					sx={{
						p: 2,
						borderRadius: 2,
						backgroundColor: isMe ? '#E6F7FF' : '#ffffff',
						border: '1px solid rgba(226,232,240,0.7)',
						maxWidth: '100%'
					}}
				>
					<Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography>
				</Paper>
				{time && (
					<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>{time}</Typography>
				)}
			</Box>
			{isMe && (
				<Avatar sx={{ width: 36, height: 36, bgcolor: '#1976d2' }}>Me</Avatar>
			)}
		</Box>
	);
};

export default function CommunicationLayout() {
	const [tab, setTab] = React.useState(0);
	const [selected, setSelected] = React.useState<number | null>(conversations[0]?.id ?? null);
	const [message, setMessage] = React.useState('');

	const current = conversations.find(c => c.id === selected) || conversations[0];

	return (
		<Box sx={{ display: 'flex', height: '100%', minHeight: '600px', bgcolor: '#F7FAFC' }}>
			{/* Left Sidebar */}
			<Box sx={{ width: 300, borderRight: '1px solid rgba(0,0,0,0.06)', bgcolor: '#fff' }}>
				<Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
					<Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons={false}>
						<Tab label="All" />
						<Tab label="From Patients" />
						<Tab label="From Doctors" />
						<Tab label="From Admin" />
						<Tab label="Urgent" />
						<Tab label="Draft" />
					</Tabs>
				</Box>

						<Box sx={{ maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
							{conversations.map(conv => (
								<Box
									key={conv.id}
									onClick={() => setSelected(conv.id)}
									sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', p: 2, cursor: 'pointer', bgcolor: conv.id === selected ? 'rgba(0,143,255,0.03)' : 'transparent' }}
								>
									<Avatar src="https://randomuser.me/api/portraits/women/65.jpg" />
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography sx={{ fontWeight: 600 }}>{conv.title}</Typography>
										<Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{conv.preview}</Typography>
									</Box>
									<Typography variant="caption" color="text.secondary">{conv.time.split(',')[0]}</Typography>
								</Box>
							))}
						</Box>
			</Box>

			{/* Main Content */}
			<Box sx={{ flex: 1, p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
				<Box>
					<Typography variant="h6">{current?.title}</Typography>
					<Typography variant="caption" color="text.secondary">{current?.subtitle}</Typography>
				</Box>

				<Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
					{/* messages */}
					<Box sx={{ mt: 1 }}>
						<MessageBubble
							text={`Hello Ms. Jane Cooper\nI'd like to discuss your recent test results and follow up on the medication adjustments we made during your last appointment. Your blood pressure readings have improved, but I'd like to monitor them for another two weeks.`}
							time="Jul 23, 9:30 AM"
							from="them"
						/>

						<MessageBubble
							text={`Good morning Dr. Williams,\nThank you for the update. I've been taking my medication as prescribed and recording my blood pressure readings twice daily.\n\nI've noticed some dizziness in the mornings - is this a normal side effect of the new medication? Should I be concerned?\n\nI've attached my test reports for your review.`}
							time="Jul 23, 10:45 AM"
							from="me"
						/>
					</Box>
				</Box>

				<Typography variant="caption" color="text.secondary">This conversation is protected by HIPAA regulations.</Typography>

				{/* Input area */}
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
					<Paper sx={{ display: 'flex', alignItems: 'center', p: '6px 10px', flex: 1, borderRadius: '28px', boxShadow: '0 6px 24px rgba(13,110,253,0.08)' }}>
						<IconButton aria-label="attach" size="small">
							<AttachFileIcon />
						</IconButton>
									<TextField
										placeholder="Say something..."
										variant="outlined"
										value={message}
										onChange={(e) => setMessage(e.target.value)}
										fullWidth
										size="small"
										sx={{ ml: 1 }}
									/>
						<IconButton color="primary" sx={{ bgcolor: '#009FF5', ml: 1, width: 44, height: 44 }}>
							<SendIcon sx={{ color: '#fff', transform: 'rotate(90deg)' }} />
						</IconButton>
					</Paper>

					<Button variant="outlined" sx={{ whiteSpace: 'nowrap' }}>Save as a Draft</Button>
				</Box>

				<Typography variant="caption" color="text.secondary">This message is protected by HIPAA regulations. Patients data is encrypted.</Typography>
			</Box>
		</Box>
	);
}
